import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Check, ChevronRight, Crown, Edit3, Heart, Lightbulb, Plus, RefreshCw, Sparkles, Star,
  Target, User, UserPlus, Users, Wand2
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import ShweebumCat from "../ShweebumCat";

export const SuggestCharactersModal = ({
  open,
  onOpenChange,
  characterMode,
  setCharacterMode,
  characterChapter,
  setCharacterChapter,
  customCharacter,
  setCustomCharacter,
  handleSuggestCharacters,
  handleInsertCharacter,
  handleInsertCustomCharacter,
  assistantLoading,
  assistantResults,
  characterInsertProgress,
  project,
  projectId,
  API,
}) => {
  return (
            <Dialog open={open} onOpenChange={(open) => {
        onOpenChange(open);
        if (!open) {
          setCharacterMode('choose');
          setCustomCharacter({ name: '', age: '', sex: '', characteristics: '', introductionMethod: '' });
          setCharacterChapter('');
        }
      }}>
        <DialogContent className="sm:max-w-5xl flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-literary flex items-center gap-2">
              <Users className="w-5 h-5 text-green-600" />
              {characterMode === 'choose' ? 'Add a Character' : 
               characterMode === 'create' ? 'Create Your Character' : 
               'Character Suggestions'}
            </DialogTitle>
            <DialogDescription>
              {characterMode === 'choose' ? 'How would you like to add a new character?' :
               characterMode === 'create' ? 'Tell Shweebum about your character' :
               'Deep, meaningful characters that would enhance your story'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
            {/* Step 1: Choose Mode */}
            {characterMode === 'choose' && (
              <div className="space-y-4">
                <Card 
                  className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => setCharacterMode('create')}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-blue-100 rounded-full">
                        <Edit3 className="w-6 h-6 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg text-blue-900">I Have a Character in Mind</h3>
                        <p className="text-sm text-blue-700">Create your own character with custom details and let Shweebum introduce them into your story</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-blue-400" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card 
                  className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => {
                    setCharacterMode('suggest');
                    handleSuggestCharacters();
                  }}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-green-100 rounded-full">
                        <Sparkles className="w-6 h-6 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg text-green-900">Let Shweebum Suggest</h3>
                        <p className="text-sm text-green-700">Get AI-generated character suggestions based on your story's needs</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-green-400" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Step 2a: Create Custom Character */}
            {characterMode === 'create' && !characterChapter && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="char-name">Character Name *</Label>
                    <Input
                      id="char-name"
                      placeholder="e.g., Sarah Mitchell"
                      value={customCharacter.name}
                      onChange={(e) => setCustomCharacter({...customCharacter, name: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="char-age">Age</Label>
                    <Input
                      id="char-age"
                      placeholder="e.g., 34"
                      value={customCharacter.age}
                      onChange={(e) => setCustomCharacter({...customCharacter, age: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="char-sex">Gender</Label>
                  <Select 
                    value={customCharacter.sex} 
                    onValueChange={(v) => setCustomCharacter({...customCharacter, sex: v})}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                      <SelectItem value="Non-binary">Non-binary</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="char-traits">Defining Characteristics *</Label>
                  <Textarea
                    id="char-traits"
                    placeholder="e.g., A sharp-witted detective with a mysterious past. She has a scar above her left eyebrow and always wears a silver locket."
                    value={customCharacter.characteristics}
                    onChange={(e) => setCustomCharacter({...customCharacter, characteristics: e.target.value})}
                    className="mt-1"
                    rows={3}
                  />
                </div>
                
                <div>
                  <Label htmlFor="char-intro">How Should They Be Introduced?</Label>
                  <Textarea
                    id="char-intro"
                    placeholder="e.g., She should appear at the crime scene, examining evidence that others have missed. Her presence should immediately create tension with the protagonist."
                    value={customCharacter.introductionMethod}
                    onChange={(e) => setCustomCharacter({...customCharacter, introductionMethod: e.target.value})}
                    className="mt-1"
                    rows={2}
                  />
                </div>
                
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" onClick={() => setCharacterMode('choose')} className="flex-1">
                    Back
                  </Button>
                  <Button 
                    onClick={() => setCharacterChapter('select')}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    disabled={!customCharacter.name || !customCharacter.characteristics}
                  >
                    Next: Choose When to Introduce
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2b: Select Chapter for Introduction */}
            {characterMode === 'create' && characterChapter === 'select' && (
              <div className="space-y-4">
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-blue-900">{customCharacter.name}</h4>
                    <p className="text-sm text-blue-700 mt-1">{customCharacter.characteristics}</p>
                  </CardContent>
                </Card>
                
                <div>
                  <Label className="text-base font-medium">When should {customCharacter.name} be introduced?</Label>
                  <div className="grid grid-cols-1 gap-2 mt-3">
                    <Button 
                      variant="outline"
                      className="justify-start text-left h-auto py-3"
                      onClick={() => setCharacterChapter('now')}
                    >
                      <div>
                        <span className="font-medium">Right Now</span>
                        <p className="text-xs text-muted-foreground">Insert them into the current section of your story</p>
                      </div>
                    </Button>
                    
                    {project?.chapter_outline?.length > 0 && (
                      <>
                        <p className="text-sm text-muted-foreground mt-2">Or choose a specific chapter:</p>
                        <div className="max-h-48 overflow-y-auto space-y-1">
                          {project.chapter_outline.map((chapter) => (
                            <Button 
                              key={chapter.number}
                              variant="outline"
                              className="w-full justify-start text-left h-auto py-2"
                              onClick={() => setCharacterChapter(`Chapter ${chapter.number}`)}
                            >
                              <span className="text-sm">Chapter {chapter.number}: {chapter.title}</span>
                            </Button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
                
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" onClick={() => setCharacterChapter('')} className="flex-1">
                    Back
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Ready to Insert Custom Character */}
            {characterMode === 'create' && characterChapter && characterChapter !== 'select' && (
              <div className="space-y-4">
                {/* Progress indicator when generating */}
                {assistantLoading && characterInsertProgress.part > 0 && (
                  <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <ShweebumCat size="sm" speaking />
                        <div className="flex-1">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-sm font-medium text-blue-900">
                              Creating {customCharacter.name}'s Story
                            </span>
                            <span className="text-sm font-bold text-blue-700">
                              {characterInsertProgress.part}/{characterInsertProgress.total}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                            <div 
                              className="h-3 rounded-full transition-all duration-500 ease-out"
                              style={{
                                width: `${(characterInsertProgress.part / characterInsertProgress.total) * 100}%`,
                                background: `linear-gradient(90deg, 
                                  #22c55e ${0}%, 
                                  #3b82f6 ${33}%, 
                                  #8b5cf6 ${66}%, 
                                  #ec4899 ${100}%)`
                              }}
                            />
                          </div>
                          <p className="text-xs text-blue-600 mt-2 italic">
                            {characterInsertProgress.message}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-blue-500 text-center">
                        Shweebum is weaving {customCharacter.name} into your story in 5 parts to ensure quality...
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Character preview card */}
                {!assistantLoading && (
                  <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-300">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-green-100 rounded-full">
                          <UserPlus className="w-6 h-6 text-green-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-lg text-green-900">{customCharacter.name}</h4>
                          <p className="text-sm text-green-700">{customCharacter.age ? `${customCharacter.age} years old` : ''} {customCharacter.sex || ''}</p>
                        </div>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="bg-card/60 p-3 rounded-lg">
                          <strong className="text-green-800">Characteristics:</strong>
                          <p className="text-green-700 mt-1">{customCharacter.characteristics}</p>
                        </div>
                        
                        {customCharacter.introductionMethod && (
                          <div className="bg-card/60 p-3 rounded-lg">
                            <strong className="text-green-800">Introduction:</strong>
                            <p className="text-green-700 mt-1">{customCharacter.introductionMethod}</p>
                          </div>
                        )}
                        
                        <div className="bg-card/60 p-3 rounded-lg">
                          <strong className="text-green-800">Timing:</strong>
                          <p className="text-green-700 mt-1">{characterChapter === 'now' ? 'Insert into current section' : `Introduce in ${characterChapter}`}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                <Button 
                  className="w-full h-12 text-lg bg-green-600 hover:bg-green-700"
                  onClick={handleInsertCustomCharacter}
                  disabled={assistantLoading}
                >
                  {assistantLoading ? (
                    <>
                      <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                      Weaving {customCharacter.name} Into Your Story...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-5 h-5 mr-2" />
                      Introduce {customCharacter.name}
                    </>
                  )}
                </Button>
                
                {!assistantLoading && (
                  <Button variant="outline" onClick={() => setCharacterChapter('select')} className="w-full">
                    Back to Timing
                  </Button>
                )}
              </div>
            )}

            {/* Shweebum Suggestions Mode */}
            {characterMode === 'suggest' && (
              <>
                {/* Show progress bar when inserting a character */}
                {assistantLoading && characterInsertProgress.part > 0 && (
                  <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 mb-4">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <ShweebumCat size="sm" speaking />
                        <div className="flex-1">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-sm font-medium text-blue-900">
                              Weaving Character Into Your Story
                            </span>
                            <span className="text-sm font-bold text-blue-700">
                              {characterInsertProgress.part}/{characterInsertProgress.total}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                            <div 
                              className="h-3 rounded-full transition-all duration-500 ease-out"
                              style={{
                                width: `${(characterInsertProgress.part / characterInsertProgress.total) * 100}%`,
                                background: `linear-gradient(90deg, 
                                  #22c55e ${0}%, 
                                  #3b82f6 ${33}%, 
                                  #8b5cf6 ${66}%, 
                                  #ec4899 ${100}%)`
                              }}
                            />
                          </div>
                          <p className="text-xs text-blue-600 mt-2 italic">
                            {characterInsertProgress.message}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-blue-500 text-center">
                        Generating in 5 parts to ensure quality and avoid timeouts...
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Show cat animation when generating character suggestions */}
                {assistantLoading && characterInsertProgress.part === 0 && (
                  <div className="text-center py-8">
                    <ShweebumCat size="md" speaking />
                    <p className="text-muted-foreground mt-4">Creating compelling characters...</p>
                  </div>
                )}
                
                {assistantResults && Array.isArray(assistantResults) && (
                  <div className="space-y-4">
                    {assistantResults.map((char, idx) => (
                      <Card key={idx} className="bg-card border-green-200">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-semibold text-lg text-foreground">{char.name}</h4>
                                <Badge variant="outline" className="text-xs">{char.role}</Badge>
                              </div>
                              <p className="text-sm text-foreground mb-2">{char.description}</p>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div className="bg-background p-2 rounded">
                                  <strong>Backstory:</strong> {char.backstory}
                                </div>
                                <div className="bg-background p-2 rounded">
                                  <strong>Motivation:</strong> {char.motivation}
                                </div>
                              </div>
                              <p className="text-xs text-muted-foreground mt-2">
                                <strong>Story conflict:</strong> {char.conflict}
                              </p>
                              <p className="text-xs text-green-700 mt-1 italic">
                                <strong>Introduction:</strong> {char.introduction_idea}
                              </p>
                            </div>
                          </div>
                          <Button 
                            className="mt-3 w-full bg-green-600 hover:bg-green-700"
                            onClick={() => handleInsertCharacter(char)}
                            disabled={assistantLoading}
                          >
                            <UserPlus className="w-4 h-4 mr-2" />
                            Insert {char.name} Into Story
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
                
                <div className="flex gap-2 mt-4">
                  <Button 
                    variant="outline" 
                    onClick={() => setCharacterMode('choose')}
                  >
                    Back
                  </Button>
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={handleSuggestCharacters}
                    disabled={assistantLoading}
                  >
                    Generate New Characters
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>


  );
};
